import { createTheme, Theme } from '@mui/material'
import config from '../config'
import orgConfig, { defaultConfig } from '../config/orgConfig'
import baseTheme from '../styles/theme'
import { OrgConfig } from '../types/OrgConfig'
import {
  isValidOrganisationId,
  normaliseOrganisationId,
  OrganisationResolution,
} from './organisationId'

const { augmentColor } = createTheme().palette

type OrgEntry = OrgConfig[string]

// Unset or empty env ids would otherwise become the keys "undefined" and "".
const organisationIndex: Record<string, OrgEntry> = Object.entries(
  orgConfig,
).reduce<Record<string, OrgEntry>>((index, [id, entry]) => {
  const normalised = normaliseOrganisationId(id)
  return isValidOrganisationId(normalised)
    ? { ...index, [normalised]: entry }
    : index
}, {})

// The validity gate also keeps prototype keys such as "constructor" out of the lookup.
const lookup = (organisationId: string | null): OrgEntry | undefined => {
  const normalised = normaliseOrganisationId(organisationId)
  return isValidOrganisationId(normalised)
    ? organisationIndex[normalised]
    : undefined
}

export class ConfigUtils {
  static defaultKnowledgeHubUrl = defaultConfig.links.knowledgeHub

  static defaultTheme = defaultConfig.theme

  static defaultName = defaultConfig.name

  static resolveOrganisation(received: string | null): OrganisationResolution {
    if (received === null) {
      return {
        status: 'missing',
        organisationId: normaliseOrganisationId(config.manualTestingOrgId),
        received: null,
      }
    }

    const organisationId = normaliseOrganisationId(received)
    if (!isValidOrganisationId(organisationId)) {
      return { status: 'malformed', organisationId: null, received }
    }

    return {
      status: lookup(organisationId) ? 'configured' : 'unconfigured',
      organisationId,
      received,
    }
  }

  static getNameForOrganisation(organisationId: string): string {
    return lookup(organisationId)?.name || this.defaultName
  }

  static getKnowledgeHubLinkForOrganisation(organisationId: string): string {
    return (
      lookup(organisationId)?.links.knowledgeHub || this.defaultKnowledgeHubUrl
    )
  }

  static getDeepLinkURIForOrganisation(
    organisationId: string,
  ): string | undefined {
    return lookup(organisationId)?.links.deepLinkURI
  }

  static getThemeForOrganisation(organisationId: string | null) {
    return lookup(organisationId)?.theme ?? this.defaultTheme
  }

  static createThemeForOrganisationParam(received: string | null): Theme {
    return this.createThemeForOrganisation(
      this.resolveOrganisation(received).organisationId,
    )
  }

  static createThemeForOrganisation(organisationId: string | null): Theme {
    const orgPalette = this.getThemeForOrganisation(organisationId)?.palette
    return createTheme(baseTheme, {
      palette: {
        ...orgPalette,
        ...(orgPalette?.primary?.main
          ? {
              primary: augmentColor({
                color: { main: orgPalette.primary.main },
              }),
            }
          : {}),
      },
    })
  }
}
